import { renderDaegeum, renderNey } from "./aerophoneModels";
import { renderErhu } from "./bowedStringModels";
import { renderYidakiDrone } from "./droneTractModels";
import { advancedInstrumentIds, getInstrumentModelProfile, getVariantModelProfile } from "./instrumentModelProfiles";
import { renderJanggu, renderTabla } from "./membraneModalModels";
import { renderGamelanSaron, renderMbira } from "./metalModalModels";
import { renderGayageum, renderKora, renderOud, renderSitar } from "./pluckedStringModels";
import type { Instrument, InstrumentVariant } from "../../data/instruments";
import type { AdvancedSynthesisInput, AdvancedVoiceHandle, InstrumentModelProfile } from "./types";

type Renderer = (input: AdvancedSynthesisInput) => AdvancedVoiceHandle;

const renderers: Record<string, Renderer> = {
  gayageum: renderGayageum,
  erhu: renderErhu,
  oud: renderOud,
  sitar: renderSitar,
  daegeum: renderDaegeum,
  ney: renderNey,
  janggu: renderJanggu,
  tabla: renderTabla,
  mbira: renderMbira,
  kora: renderKora,
  yidaki_didgeridoo: renderYidakiDrone,
  gamelan_saron: renderGamelanSaron
};

export function canRenderAdvanced(instrumentId: string): boolean {
  return Boolean(renderers[instrumentId] && getInstrumentModelProfile(instrumentId));
}

export function getAdvancedModelProfile(instrumentId: string): InstrumentModelProfile | undefined {
  return getInstrumentModelProfile(instrumentId);
}

export function renderAdvanced(input: AdvancedSynthesisInput): AdvancedVoiceHandle | null {
  const renderer = renderers[input.instrumentId];

  if (!renderer) {
    return null;
  }

  try {
    return renderer(input);
  } catch (error) {
    if (typeof console !== "undefined") {
      console.warn(`Advanced synthesis failed for ${input.instrumentId}; falling back to scaffold.`, error);
    }

    return null;
  }
}

export function assertAdvancedModelCoverage(instruments: Instrument[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const instrumentIds = instruments.map((instrument) => instrument.id);

  for (const instrumentId of instrumentIds) {
    if (!canRenderAdvanced(instrumentId)) {
      errors.push(`${instrumentId} is not registered in the advanced model router.`);
    }
  }

  for (const instrumentId of advancedInstrumentIds) {
    if (!instrumentIds.includes(instrumentId)) {
      errors.push(`${instrumentId} has an advanced model profile but no instrument data.`);
    }
  }

  for (const instrument of instruments) {
    const profile = getInstrumentModelProfile(instrument.id);

    if (!profile) {
      continue;
    }

    for (const variant of instrument.variants) {
      if (!profile.variantProfiles[variant.id]) {
        errors.push(`${instrument.id}/${variant.id} is missing a variant model profile.`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export function getVariantAdvancedProfile(variant: InstrumentVariant) {
  return getVariantModelProfile(variant.instrumentId, variant.id);
}
